import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { lockInScore, unlockScore, startRevealing, sendPoke } from '../../services/firebase/games';
import { aggregateChatterScores } from '../../services/firebase/chatters';
import { CHATTERS_ID } from '../../constants/game';
import { logError } from '../../utils/logger';
import HostScreenView from '../../components/HostScreenView';
import { useWebRTC } from '../../context/WebRTCContext';
import PlayerVideoCard, { KickDialog } from '../../components/PlayerVideoCard';
import { getGridCols, getColCount } from '../../utils/videoGrid';

/**
 * Derives a lock-time string ("12.345s") from score timestamps.
 * Returns null when data is missing or the player hasn't locked in.
 */
function getLockTime(scoreDoc, scoringStartedAt) {
  if (!scoreDoc?.lockedIn || !scoreDoc?.submittedAt || !scoringStartedAt) return null;
  const ms = scoreDoc.submittedAt.toMillis() - scoringStartedAt.toMillis();
  return (ms / 1000).toFixed(3) + 's';
}

// Default and constraint constants for the floating camera panel
const FLOATING_PANEL_INITIAL_W = 340;
const FLOATING_PANEL_MIN_W = 180;
const FLOATING_PANEL_MAX_W = 600;

export default function ScoringPhase({
  game, round, scores, isHost, chatterVotes, playerAvatars,
  isMaximized, onIsMaximizedChange,
  floatingGridPos, onFloatingGridPosChange,
  floatingPanelW, onFloatingPanelWChange,
  displayMode = false,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const webrtc = useWebRTC();

  const [myScore, setMyScore] = useState('');
  const [predictions, setPredictions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [startingReveal, setStartingReveal] = useState(false);
  // Screen share visibility
  const [showScreenShare, setShowScreenShare] = useState(true);
  // Floating camera panel local-only state (not persisted across rounds)
  const [floatingGridVisible, setFloatingGridVisible] = useState(true);
  // Convenience aliases so the rest of the component is unchanged
  const setIsMaximized = onIsMaximizedChange;
  const setFloatingGridPos = onFloatingGridPosChange;
  const setFloatingPanelW = onFloatingPanelWChange;
  // Kick dialog
  const [kickConfirmUid, setKickConfirmUid] = useState(null);
  const [kicking, setKicking] = useState(false);

  // Poke feature
  const [pokeTargetUid, setPokeTargetUid] = useState(null);
  const [pokeText, setPokeText] = useState('');
  const [pokeSending, setPokeSending] = useState(false);
  const [canPokeByTime, setCanPokeByTime] = useState(false);
  const [incomingPokeText, setIncomingPokeText] = useState(null);
  // Tracks which poke keys have been shown so the same poke isn't displayed twice
  const shownPokesRef = useRef(new Set());

  // Track sm breakpoint (≥640 px) for grid-template-rows computation.
  const [isSm, setIsSm] = useState(() => window.innerWidth >= 640);
  useEffect(() => {
    const handler = () => setIsSm(window.innerWidth >= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Measure available height for the video grid container (normal mode only).
  const gridContainerRef = useRef(null);
  const [gridContainerH, setGridContainerH] = useState(0);
  useEffect(() => {
    if (isMaximized) return;
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setGridContainerH(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMaximized]);

  // Track the floating panel's rendered height (for accurate viewport clamping and aspect-ratio
  // preservation during corner resize). videoGridRef points to the floating panel outer div when
  // isMaximized, so we reuse it here.
  const panelHeightRef = useRef(0);
  const videoGridRef = useRef(null);
  // Ref for the drag handle — used to attach a non-passive touchstart listener so that
  // e.preventDefault() can block the browser's pull-to-refresh on mobile.
  const dragHandleRef = useRef(null);
  useEffect(() => {
    if (!isMaximized) return;
    const el = videoGridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      panelHeightRef.current = entry.contentRect.height;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMaximized]);

  // Clamp the floating panel position whenever the viewport is resized so it
  // can never go partially or fully off-screen.
  useEffect(() => {
    if (!isMaximized) return;
    function handleResize() {
      const panelH = panelHeightRef.current;
      setFloatingGridPos((pos) => {
        if (!pos) return pos;
        return {
          x: Math.max(0, Math.min(window.innerWidth - floatingPanelW, pos.x)),
          y: Math.max(0, Math.min(Math.max(0, window.innerHeight - panelH), pos.y)),
        };
      });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMaximized, floatingPanelW, setFloatingGridPos]);

  // Attach a non-passive touchstart listener to the drag handle so e.preventDefault() can block
  // the browser's pull-to-refresh gesture on mobile. React's onTouchStart is passive and cannot
  // call preventDefault(), so this must be done imperatively.
  useEffect(() => {
    if (!isMaximized) return;
    const el = dragHandleRef.current;
    if (!el) return;
    function blockPullToRefresh(e) {
      e.preventDefault();
    }
    el.addEventListener('touchstart', blockPullToRefresh, { passive: false });
    return () => el.removeEventListener('touchstart', blockPullToRefresh);
  }, [isMaximized]);

  const myUid = user?.uid;
  const regularPlayerIds = game.playerIds.filter((id) => id !== CHATTERS_ID);
  const otherPlayers = game.playerIds.filter((id) => id !== myUid);

  const myScoreDoc = scores[myUid];
  const myLockedScore = myScoreDoc?.lockedIn ? myScoreDoc : null;

  // Reset shown-pokes tracker when the round changes
  useEffect(() => {
    shownPokesRef.current = new Set();
  }, [round.id]);

  // Compute canPokeByTime: true once scoring started 90s ago AND I've been locked in 10s
  useEffect(() => {
    const scoringAt = round.scoringStartedAt?.toMillis?.();
    const isLocked = myScoreDoc?.lockedIn;
    const lockedAt = isLocked ? myScoreDoc?.submittedAt?.toMillis?.() : null;

    if (!scoringAt || !isLocked || !lockedAt) {
      setCanPokeByTime(false);
      return;
    }

    const check = () => {
      const now = Date.now();
      return (now - scoringAt >= 90_000) && (now - lockedAt >= 10_000);
    };

    if (check()) {
      setCanPokeByTime(true);
      return;
    }

    setCanPokeByTime(false);
    const now = Date.now();
    const delay = Math.max(
      Math.max(0, 90_000 - (now - scoringAt)),
      Math.max(0, 10_000 - (now - lockedAt))
    ) + 100;
    const timer = setTimeout(() => setCanPokeByTime(check()), delay);
    return () => clearTimeout(timer);
  }, [round.scoringStartedAt, myScoreDoc?.submittedAt, myScoreDoc?.lockedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for incoming pokes targeting this player
  const roundPokesJson = JSON.stringify(round.pokes ?? {});
  useEffect(() => {
    const pokes = round.pokes ?? {};
    for (const [key, pokeData] of Object.entries(pokes)) {
      if (key.endsWith(`_${myUid}`) && !shownPokesRef.current.has(key) && pokeData?.text) {
        shownPokesRef.current.add(key);
        setIncomingPokeText(pokeData.text);
        break;
      }
    }
  }, [roundPokesJson, myUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayPlayers = [
    ...regularPlayerIds,
    ...(game.chattersEnabled ? [CHATTERS_ID] : []),
  ];
  const gridCols = getColCount(displayPlayers.length, isSm);
  const gridRows = Math.max(1, Math.ceil(displayPlayers.length / gridCols));

  // Compute max-width so each card fits at 16:9 within the available container height (normal mode).
  const GAP_PX = 8;
  const PAD_BOTTOM = 8;
  const MIN_CARD_WIDTH = 160;
  const MIN_CARD_HEIGHT = 90;
  const rowH = gridContainerH > 0
    ? Math.max((gridContainerH - PAD_BOTTOM - (gridRows - 1) * GAP_PX) / gridRows, MIN_CARD_HEIGHT)
    : MIN_CARD_HEIGHT;
  const cardW = Math.max(rowH * (16 / 9), MIN_CARD_WIDTH);
  const gridMaxWidth = cardW * gridCols + (gridCols - 1) * GAP_PX;

  // UID of the card currently receiving keyboard score entry
  const [activeTypingUid, setActiveTypingUid] = useState(null);

  // Deactivate keyboard score capture when clicking outside the video grid
  useEffect(() => {
    if (!activeTypingUid) return;
    function handleMouseDown(e) {
      if (videoGridRef.current && !videoGridRef.current.contains(e.target)) {
        setActiveTypingUid(null);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [activeTypingUid]);

  // Keyboard score entry
  useEffect(() => {
    if (!activeTypingUid || myLockedScore) return;

    function handleKeyDown(e) {
      const isDigit = /^[0-9]$/.test(e.key);
      const isDot = e.key === '.';
      if (e.key === 'Escape') { setActiveTypingUid(null); return; }
      if (!isDigit && !isDot) return;
      e.preventDefault();

      const isMe = activeTypingUid === myUid;
      const current = isMe ? myScore : (predictions[activeTypingUid] ?? '');
      const set = (v) => isMe ? setMyScore(v) : handlePrediction(activeTypingUid, v);

      if (isDot) {
        if (/^[0-9]$/.test(current)) set(current + '.');
      } else if (current.endsWith('.')) {
        set(current + e.key);
      } else {
        set(e.key);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTypingUid, myScore, predictions, myUid, myLockedScore]);

  const lockedCount = regularPlayerIds.filter((id) => scores[id]?.lockedIn).length;
  const allRegularLocked = regularPlayerIds.every((id) => scores[id]?.lockedIn);

  const lockedChatters = Object.values(chatterVotes || {}).filter((v) => v.lockedIn).length;
  const totalChatters = Object.keys(chatterVotes || {}).length;

  // WebRTC destructure — null-safe
  const {
    cameraEnabled,
    micEnabled,
    toggleCamera,
    toggleMic,
    localCameraStream,
    remoteStreamsByUid,
    playerMediaStates,
    localMutes,
    localVolumes,
    localHidden,
    muteRemote,
    unmuteRemote,
    setRemoteVolume,
    hideRemoteCamera,
    showRemoteCamera,
    kickPlayer: webrtcKickPlayer,
    hostScreenStream,
    startScreenShare,
    stopScreenShare,
    isScreenSharing,
  } = webrtc || {};

  // Restore form values when unlocking
  useEffect(() => {
    if (myScoreDoc && !myScoreDoc.lockedIn) {
      if (myScoreDoc.actualScore != null) setMyScore(String(myScoreDoc.actualScore));
      if (myScoreDoc.predictions) {
        setPredictions(
          Object.fromEntries(
            Object.entries(myScoreDoc.predictions).map(([k, v]) => [k, String(v)])
          )
        );
      }
      setSubmitting(false);
    }
  }, [myScoreDoc?.lockedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePrediction(uid, val) {
    setPredictions((prev) => ({ ...prev, [uid]: val }));
  }

  async function handleUnlock() {
    setUnlocking(true);
    try {
      await unlockScore(game.id, round.id, myUid);
    } catch (err) {
      logError(err);
    } finally {
      setUnlocking(false);
    }
  }

  async function handleLockIn() {
    const score = parseFloat(myScore);
    if (isNaN(score) || score < 0 || score > 10) return;
    for (const uid of otherPlayers) {
      const p = parseFloat(predictions[uid]);
      if (isNaN(p) || p < 0 || p > 10) return;
    }
    setSubmitting(true);
    try {
      const parsedPredictions = {};
      for (const uid of otherPlayers) {
        parsedPredictions[uid] = parseFloat(predictions[uid]);
      }
      await lockInScore(game.id, round.id, myUid, {
        actualScore: parseFloat(myScore),
        predictions: parsedPredictions,
      });
    } catch (err) {
      logError(err);
      setSubmitting(false);
    }
  }

  async function handleStartRevealing() {
    setStartingReveal(true);
    try {
      if (game.chattersEnabled) {
        await aggregateChatterScores(
          game.id,
          round.id,
          chatterVotes || {},
          regularPlayerIds
        );
      }
      await startRevealing(game.id, round.id, game.playerIds, round.chooserId);
    } catch (err) {
      logError(err);
      setStartingReveal(false);
    }
  }

  async function handleKick(uid) {
    setKicking(true);
    try {
      await webrtcKickPlayer(uid);
    } catch (err) {
      logError(err);
    } finally {
      setKicking(false);
      setKickConfirmUid(null);
    }
  }

  async function handleSendPoke(targetUid) {
    if (!pokeText.trim()) return;
    setPokeSending(true);
    try {
      await sendPoke(game.id, round.id, myUid, targetUid, pokeText.trim());
      setPokeTargetUid(null);
      setPokeText('');
    } catch (err) {
      logError(err);
    } finally {
      setPokeSending(false);
    }
  }

  function handleMaximize() {
    setIsMaximized(true);
    setFloatingGridVisible(true);
    if (!floatingGridPos) {
      setFloatingGridPos({
        x: Math.max(0, window.innerWidth - floatingPanelW - 16),
        y: 80,
      });
    }
  }

  function handleRestore() {
    setIsMaximized(false);
  }

  // ── Floating panel: drag to reposition ───────────────────────────────────
  // Clamps so the full panel always remains within the viewport.
  function handleFloatingDragStart(e) {
    if (e.button !== undefined && e.button !== 0) return;
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    const startPosX = floatingGridPos?.x ?? 0;
    const startPosY = floatingGridPos?.y ?? 0;
    const currentW = floatingPanelW;
    const currentH = panelHeightRef.current;

    function move(ev) {
      ev.preventDefault();
      const cx = ev.touches?.[0]?.clientX ?? ev.clientX;
      const cy = ev.touches?.[0]?.clientY ?? ev.clientY;
      setFloatingGridPos({
        x: Math.max(0, Math.min(window.innerWidth - currentW, startPosX + cx - clientX)),
        y: Math.max(0, Math.min(window.innerHeight - currentH, startPosY + cy - clientY)),
      });
    }
    function end() {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
    }
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
  }

  // ── Floating panel: corner drag to resize ────────────────────────────────
  // Width is the control variable; height follows from content (cards are aspect-video).
  // For top/left corners, position is also adjusted so the opposite corner stays anchored.
  // Aspect ratio (at drag-start) is used to estimate height changes for y-clamping.
  function handleCornerResizeStart(e, corner) {
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();

    const startW = floatingPanelW;
    const startH = panelHeightRef.current;
    const startX = floatingGridPos?.x ?? 0;
    const startY = floatingGridPos?.y ?? 0;
    const startClientX = e.touches?.[0]?.clientX ?? e.clientX;

    const isLeft = corner === 'tl' || corner === 'bl';
    const isTop  = corner === 'tl' || corner === 'tr';
    // Guard against divide-by-zero if panel hasn't been measured yet
    const aspectRatio = startH > 0 ? startW / startH : null;

    function move(ev) {
      ev.preventDefault();
      const cx = ev.touches?.[0]?.clientX ?? ev.clientX;
      const dx = cx - startClientX;

      // Derive new width from horizontal drag; left corners grow leftward
      const rawW = isLeft ? startW - dx : startW + dx;
      const newW = Math.max(
        FLOATING_PANEL_MIN_W,
        Math.min(FLOATING_PANEL_MAX_W, rawW)
      );
      const widthDelta = newW - startW;

      // Adjust x so the right/bottom-right anchor stays fixed for left corners
      let newX = isLeft ? startX - widthDelta : startX;

      // Adjust y so the bottom anchor stays fixed for top corners
      let newY = startY;
      if (isTop && aspectRatio !== null) {
        const estimatedNewH = newW / aspectRatio;
        newY = startY - (estimatedNewH - startH);
      }

      // Clamp fully within viewport
      const estimatedH = aspectRatio !== null ? newW / aspectRatio : startH;
      newX = Math.max(0, Math.min(window.innerWidth - newW, newX));
      newY = Math.max(0, Math.min(window.innerHeight - estimatedH, newY));

      setFloatingPanelW(newW);
      setFloatingGridPos({ x: newX, y: newY });
    }
    function end() {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
    }
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
  }

  const allFilled =
    parseFloat(myScore) >= 0 &&
    parseFloat(myScore) <= 10 &&
    otherPlayers.every((uid) => parseFloat(predictions[uid]) >= 0 && parseFloat(predictions[uid]) <= 10);

  // ── Per-card overlay builders ─────────────────────────────────────────────

  function buildCardProps(uid) {
    const isMe = uid === myUid;
    const isChattersSlot = uid === CHATTERS_ID;
    const playerScore = scores[uid];
    const isPlayerLocked = playerScore?.lockedIn ?? false;
    const lockTime = isMe
      ? getLockTime(myScoreDoc, round.scoringStartedAt)
      : getLockTime(playerScore, round.scoringStartedAt);

    const value = isMe ? myScore : (predictions[uid] ?? '');
    const lockedValue = isMe
      ? myLockedScore?.actualScore
      : myLockedScore?.predictions?.[uid];

    function renderTopLeft() {
      return (
        <div className="min-w-0">
          <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
            {isMe
              ? (myLockedScore ? (user?.displayName || t('scoring.you')) : t('scoring.yourScore'))
              : (isChattersSlot ? 'Chatters' : (game.playerNames?.[uid] || uid))}
          </p>
          <p className="text-xs leading-tight drop-shadow">
            {isMe ? (
              myLockedScore ? (
                <>
                  <span style={{ color: 'var(--c-success)' }} className="font-medium">{t('scoring.lockedAt')}</span>
                  {lockTime && <span className="t-text-secondary">: {lockTime}</span>}
                </>
              ) : (
                <span className="t-text-secondary">{t('scoring.howDoYouRate')}</span>
              )
            ) : (
              isPlayerLocked ? (
                <>
                  <span style={{ color: 'var(--c-success)' }} className="font-medium">{t('scoring.lockedAt')}</span>
                  {lockTime && <span className="t-text-secondary">: {lockTime}</span>}
                </>
              ) : (
                <span className="t-text-secondary">{t('scoring.prediction')}</span>
              )
            )}
          </p>
        </div>
      );
    }

    const alreadyPoked = !!round.pokes?.[`${myUid}_${uid}`];
    const showPokeButton = !isMe && !isChattersSlot && canPokeByTime && !alreadyPoked;

    function renderTopRight() {
      if (displayMode) return null;
      if (!myLockedScore) return null;
      const display = lockedValue ?? value;
      return (
        <div className="flex items-center gap-1 shrink-0">
          {display != null && display !== '' && (
            <span className="t-text font-bold text-sm leading-tight drop-shadow whitespace-nowrap">
              {display}/10
            </span>
          )}
          {showPokeButton && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPokeTargetUid(uid); setPokeText(''); }}
              aria-label={t('scoring.pokeAria', { name: game.playerNames?.[uid] || uid })}
              title={t('scoring.pokeButton')}
              className="text-xs px-1 py-0.5 rounded bg-(--c-bg-page)/65 hover:bg-(--c-bg-page)/85 t-text-warning leading-tight drop-shadow t-focus-ring transition-colors"
            >
              👉
            </button>
          )}
        </div>
      );
    }

    function renderBottom() {
      if (displayMode) return null;
      if (myLockedScore) {
        if (!isMe) return null;
        return (
          <button
            type="button"
            onClick={handleUnlock}
            disabled={unlocking}
            className="t-text-secondary text-xs underline hover:t-text disabled:opacity-40 drop-shadow"
          >
            {unlocking ? t('scoring.unlocking') : t('scoring.changeScores')}
          </button>
        );
      }
      return (
        <ScoreSlider
          value={value}
          onChange={(v) => isMe ? setMyScore(v) : handlePrediction(uid, v)}
          label={t('scoring.scoreSlider')}
        />
      );
    }

    return {
      renderTopLeft,
      renderTopRight,
      renderBottom,
      highlight: isMe && !myLockedScore,
    };
  }

  // Shared card list used by the normal grid
  function renderCards(gridClassName, extraGridStyle) {
    return (
      <div
        ref={videoGridRef}
        className={`grid gap-2 mx-auto ${gridClassName}`}
        style={extraGridStyle}
      >
        {displayPlayers.map((uid) => {
          const isMe = uid === myUid;
          const isChattersSlot = uid === CHATTERS_ID;
          const { renderTopLeft, renderTopRight, renderBottom, highlight } = buildCardProps(uid);
          return (
            <PlayerVideoCard
              key={uid}
              uid={uid}
              name={isChattersSlot ? 'Chatters' : (game.playerNames?.[uid] || uid)}
              avatarEmoji={playerAvatars?.[uid]}
              isMe={isMe}
              isViewerChatter={false}
              isChattersSlot={isChattersSlot}
              onCardClick={!myLockedScore && !displayMode ? () => setActiveTypingUid(uid) : undefined}
              mediaState={playerMediaStates?.[uid]}
              localCameraStream={isMe ? localCameraStream : null}
              remoteStreamsByUid={!isMe ? remoteStreamsByUid?.get(uid) : null}
              cameraStreamId={playerMediaStates?.[uid]?.cameraStreamId}
              myCameraEnabled={isMe ? cameraEnabled : undefined}
              myMicEnabled={isMe ? micEnabled : undefined}
              toggleCamera={isMe ? toggleCamera : null}
              toggleMic={isMe ? toggleMic : null}
              isMuted={!isMe ? (localMutes?.[uid] ?? false) : false}
              volume={!isMe ? (localVolumes?.[uid] ?? 1) : 1}
              isHidden={!isMe ? (localHidden?.[uid] ?? false) : false}
              onMuteToggle={!isMe && !isChattersSlot ? () => (localMutes?.[uid] ? unmuteRemote?.(uid) : muteRemote?.(uid)) : null}
              onVolumeChange={!isMe && !isChattersSlot ? (v) => setRemoteVolume?.(uid, v) : null}
              onHideToggle={!isMe && !isChattersSlot ? () => (localHidden?.[uid] ? showRemoteCamera?.(uid) : hideRemoteCamera?.(uid)) : null}
              onKick={isHost && !isMe && !isChattersSlot ? () => setKickConfirmUid(uid) : null}
              inPersonMode={game.inPersonMode}
              renderTopLeft={renderTopLeft}
              renderTopRight={renderTopRight}
              renderBottom={renderBottom}
              highlight={highlight}
            />
          );
        })}
      </div>
    );
  }

  // Corner resize handle descriptors
  const CORNER_HANDLES = [
    { corner: 'tl', top: -6, left: -6,   cursor: 'nwse-resize' },
    { corner: 'tr', top: -6, right: -6,  cursor: 'nesw-resize' },
    { corner: 'bl', bottom: -6, left: -6,  cursor: 'nesw-resize' },
    { corner: 'br', bottom: -6, right: -6, cursor: 'nwse-resize' },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

      {/* ── Screen share controls + content ──────────────────────────── */}
      {!game.inPersonMode && (
        <>
          <div className="px-4 pt-2 shrink-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <button
                type="button"
                onClick={() => setShowScreenShare((v) => !v)}
                className="text-xs t-text-muted hover:t-text underline t-focus-ring rounded transition-colors"
              >
                {showScreenShare ? t('scoring.hideScreenShare') : t('scoring.showScreenShare')}
              </button>
              {!isMaximized ? (
                <button
                  type="button"
                  onClick={handleMaximize}
                  className="text-xs t-text-muted hover:t-text underline t-focus-ring rounded transition-colors"
                >
                  {t('scoring.maximiseScreenShare')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleRestore}
                  className="text-xs t-text-muted hover:t-text underline t-focus-ring rounded transition-colors"
                >
                  {t('scoring.restoreScreenShare')}
                </button>
              )}
              {isHost && isScreenSharing && (
                <button
                  type="button"
                  onClick={stopScreenShare}
                  className="text-xs t-text-error hover:opacity-80 underline t-focus-ring rounded transition-opacity"
                >
                  {t('scoring.stopScreenShare')}
                </button>
              )}
            </div>
            {/* Normal mode: screen share inline */}
            {!isMaximized && showScreenShare && (
              <HostScreenView
                stream={hostScreenStream ?? null}
                isHost={isHost}
                onRequestShare={isHost ? startScreenShare : undefined}
                onStall={!isHost ? (tier) => {
                  if (tier === 2) webrtc?.retryStreamBinding?.(game.hostId);
                  else if (tier === 3) webrtc?.reconnectPeer?.(game.hostId);
                } : undefined}
              />
            )}
          </div>

          {/* Maximised mode: screen share fills available space */}
          {isMaximized && showScreenShare && (
            <div className="flex-1 min-h-0 px-4 pb-2">
              <HostScreenView
                fill
                stream={hostScreenStream ?? null}
                isHost={isHost}
                onRequestShare={isHost ? startScreenShare : undefined}
                onStall={!isHost ? (tier) => {
                  if (tier === 2) webrtc?.retryStreamBinding?.(game.hostId);
                  else if (tier === 3) webrtc?.reconnectPeer?.(game.hostId);
                } : undefined}
              />
            </div>
          )}

          {/* Maximised mode, screen share hidden: spacer keeps actions at bottom */}
          {isMaximized && !showScreenShare && (
            <div className="flex-1 min-h-0" />
          )}
        </>
      )}

      {/* ── Player video card grid (normal mode only) ────────────────── */}
      {(!isMaximized || game.inPersonMode) && (
        <div ref={gridContainerRef} className="flex-1 min-h-0 px-3 pb-2 overflow-hidden">
          {renderCards(getGridCols(displayPlayers.length), { maxWidth: `${gridMaxWidth}px` })}
        </div>
      )}

      {/* ── Action button ─────────────────────────────────────────────── */}
      <div className="px-4 py-3 shrink-0 flex flex-col items-center">
        {displayMode ? (
          <p className="t-text-disabled text-sm text-center">{t('scoring.ratingsHidden')}</p>
        ) : !myLockedScore ? (
          <button
            onClick={handleLockIn}
            disabled={submitting || !allFilled}
            className="t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-6 py-3 transition-colors t-focus-ring"
          >
            {submitting ? t('scoring.lockingIn') : t('scoring.lockIn')}
          </button>
        ) : isHost && allRegularLocked ? (
          // In maximised mode the button lives in the floating panel header
          !isMaximized && (
            <button
              onClick={handleStartRevealing}
              disabled={startingReveal}
              className="t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-6 py-3 transition-colors t-focus-ring"
            >
              {startingReveal ? t('scoring.starting') : t('scoring.startReveal')}
            </button>
          )
        ) : !isHost && allRegularLocked ? (
          // Waiting text removed in maximised mode — status is implied
          !isMaximized && (
            <p className="t-text-disabled text-sm text-center">{t('scoring.waitingForReveal')}</p>
          )
        ) : (
          <p className="t-text-disabled text-sm text-center">{t('scoring.waitingToLockIn')}</p>
        )}

        {game.chattersEnabled && totalChatters > 0 && (
          <p className="text-xs t-text-warning text-center mt-1">
            {t('scoring.chatSubmitted', { locked: lockedChatters, total: totalChatters })}
          </p>
        )}
      </div>

      {/* ── Floating camera panel (maximised mode) ───────────────────── */}
      {isMaximized && floatingGridPos && (
        // Outer wrapper: provides positioning context for corner handles (overflow-visible)
        <div
          ref={videoGridRef}
          style={{
            position: 'fixed',
            left: floatingGridPos.x,
            top: floatingGridPos.y,
            width: floatingPanelW,
            zIndex: 50,
            userSelect: 'none',
          }}
          className="relative"
        >
          {/* Corner resize handles — visible dots at each corner */}
          {CORNER_HANDLES.map(({ corner, cursor, ...pos }) => (
            <div
              key={corner}
              onMouseDown={(e) => handleCornerResizeStart(e, corner)}
              onTouchStart={(e) => handleCornerResizeStart(e, corner)}
              style={{
                position: 'absolute',
                ...pos,
                width: 14,
                height: 14,
                borderRadius: 4,
                background: 'var(--c-border)',
                border: '1px solid var(--c-bg-surface)',
                cursor,
                zIndex: 10,
              }}
              aria-hidden="true"
            />
          ))}

          {/* Panel content */}
          <div className="t-bg-surface t-border border rounded-xl shadow-2xl overflow-hidden">
            {/* Drag handle — dragHandleRef enables non-passive touchstart for pull-to-refresh prevention */}
            <div
              ref={dragHandleRef}
              className="flex items-center gap-2 px-2 py-1.5 cursor-grab active:cursor-grabbing"
              style={{ borderBottom: '1px solid var(--c-border)' }}
              onMouseDown={handleFloatingDragStart}
              onTouchStart={handleFloatingDragStart}
            >
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={() => setFloatingGridVisible((v) => !v)}
                className="text-xs t-text-muted hover:t-text t-focus-ring rounded transition-colors leading-none shrink-0"
              >
                {floatingGridVisible ? t('scoring.hideFloatingGrid') : t('scoring.showFloatingGrid')}
              </button>
              <span className="text-xs t-text-muted select-none pointer-events-none shrink-0 ml-auto" aria-hidden="true">⠿</span>
            </div>

            {/* Camera grid */}
            {floatingGridVisible && (
              <div className="p-2">
                <div className={`grid gap-1.5 ${getGridCols(displayPlayers.length)}`}>
                  {displayPlayers.map((uid) => {
                    const isMe = uid === myUid;
                    const isChattersSlot = uid === CHATTERS_ID;
                    const { renderTopLeft, renderTopRight, renderBottom, highlight } = buildCardProps(uid);
                    return (
                      <PlayerVideoCard
                        key={uid}
                        uid={uid}
                        name={isChattersSlot ? 'Chatters' : (game.playerNames?.[uid] || uid)}
                        avatarEmoji={playerAvatars?.[uid]}
                        isMe={isMe}
                        isViewerChatter={false}
                        isChattersSlot={isChattersSlot}
                        onCardClick={!myLockedScore && !displayMode ? () => setActiveTypingUid(uid) : undefined}
                        mediaState={playerMediaStates?.[uid]}
                        localCameraStream={isMe ? localCameraStream : null}
                        remoteStreamsByUid={!isMe ? remoteStreamsByUid?.get(uid) : null}
                        cameraStreamId={playerMediaStates?.[uid]?.cameraStreamId}
                        myCameraEnabled={isMe ? cameraEnabled : undefined}
                        myMicEnabled={isMe ? micEnabled : undefined}
                        toggleCamera={isMe ? toggleCamera : null}
                        toggleMic={isMe ? toggleMic : null}
                        isMuted={!isMe ? (localMutes?.[uid] ?? false) : false}
                        volume={!isMe ? (localVolumes?.[uid] ?? 1) : 1}
                        isHidden={!isMe ? (localHidden?.[uid] ?? false) : false}
                        onMuteToggle={!isMe && !isChattersSlot ? () => (localMutes?.[uid] ? unmuteRemote?.(uid) : muteRemote?.(uid)) : null}
                        onVolumeChange={!isMe && !isChattersSlot ? (v) => setRemoteVolume?.(uid, v) : null}
                        onHideToggle={!isMe && !isChattersSlot ? () => (localHidden?.[uid] ? showRemoteCamera?.(uid) : hideRemoteCamera?.(uid)) : null}
                        onKick={isHost && !isMe && !isChattersSlot ? () => setKickConfirmUid(uid) : null}
                        inPersonMode={game.inPersonMode}
                        renderTopLeft={renderTopLeft}
                        renderTopRight={renderTopRight}
                        renderBottom={renderBottom}
                        highlight={highlight}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Start reveal — always visible below the grid so it persists when cameras are hidden */}
            {isHost && allRegularLocked && !displayMode && (
              <div style={{ borderTop: '1px solid var(--c-border)' }} className="px-2 py-1.5">
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onClick={handleStartRevealing}
                  disabled={startingReveal}
                  className="t-btn-primary disabled:opacity-50 text-xs font-semibold rounded px-2 py-1.5 transition-colors t-focus-ring w-full"
                >
                  {startingReveal ? t('scoring.starting') : t('scoring.startReveal')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kick confirmation dialog */}
      {kickConfirmUid && (
        <KickDialog
          name={game.playerNames?.[kickConfirmUid] || kickConfirmUid}
          onConfirm={() => handleKick(kickConfirmUid)}
          onCancel={() => setKickConfirmUid(null)}
          kicking={kicking}
        />
      )}

      {/* Poke text input popup */}
      {pokeTargetUid && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => { setPokeTargetUid(null); setPokeText(''); }}
        >
          <div
            className="t-bg-surface t-border border rounded-2xl p-4 w-full max-w-xs shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-semibold t-text mb-2 text-sm">
              {t('scoring.pokeTitle', { name: game.playerNames?.[pokeTargetUid] || pokeTargetUid })}
            </p>
            <input
              type="text"
              maxLength={20}
              value={pokeText}
              onChange={(e) => setPokeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendPoke(pokeTargetUid);
                if (e.key === 'Escape') { setPokeTargetUid(null); setPokeText(''); }
              }}
              placeholder={t('scoring.pokePlaceholder')}
              className="w-full t-bg-input t-border border rounded-lg px-3 py-2 t-text text-sm mb-3 t-focus-ring"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setPokeTargetUid(null); setPokeText(''); }}
                className="text-sm t-text-secondary px-3 py-1.5 rounded-lg t-bg-input t-focus-ring hover:opacity-80"
              >
                {t('scoring.pokeCancel')}
              </button>
              <button
                type="button"
                onClick={() => handleSendPoke(pokeTargetUid)}
                disabled={!pokeText.trim() || pokeSending}
                className="text-sm t-btn-primary px-3 py-1.5 rounded-lg disabled:opacity-50 t-focus-ring font-medium"
              >
                {pokeSending ? '…' : t('scoring.pokeSend')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Incoming poke overlay */}
      {incomingPokeText && (
        <PokeOverlay
          text={incomingPokeText}
          onDismiss={() => setIncomingPokeText(null)}
        />
      )}
    </div>
  );
}

// ─── Incoming poke overlay ────────────────────────────────────────────────────

function PokeOverlay({ text, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 1500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      onClick={onDismiss}
      role="alert"
      aria-live="assertive"
    >
      <style>{`
        @keyframes poke-expand {
          0%   { font-size: 3rem; opacity: 0.7; }
          100% { font-size: 20vw; opacity: 1; }
        }
      `}</style>
      <span
        style={{
          color: 'var(--c-warning)',
          fontWeight: 700,
          animation: 'poke-expand 0.5s ease-out forwards',
          display: 'block',
          maxWidth: '80vw',
          textAlign: 'center',
          lineHeight: 1.1,
          wordBreak: 'break-word',
          textShadow: '0 2px 12px rgba(0,0,0,0.6)',
          pointerEvents: 'none',
        }}
      >
        {text}
      </span>
    </div>,
    document.body
  );
}

// ─── Mobile score slider with magnified popup + numpad ───────────────────────

function ScoreSlider({ value, onChange, label }) {
  const { t } = useTranslation();
  const [isMobile] = useState(() => window.matchMedia('(pointer: coarse)').matches);

  const numVal    = value !== '' ? parseFloat(value) : 5;
  const displayVal = value !== ''
    ? (value.endsWith('.') ? value : parseFloat(value).toFixed(1))
    : '-';
  const pct = Math.max(0, Math.min(100, (numVal / 10) * 100));

  const [showPopup,    setShowPopup]    = useState(false);
  const [popupAnchorX, setPopupAnchorX] = useState(0);
  const [popupAnchorY, setPopupAnchorY] = useState(0);
  const [showNumpad,   setShowNumpad]   = useState(false);

  const sliderWrapRef = useRef(null);
  const touchMetaRef  = useRef(null);
  // Keep a ref to pct so the touchstart closure can read the latest value
  const pctRef = useRef(pct);
  useEffect(() => { pctRef.current = pct; }, [pct]);

  function getThumbClientX() {
    const el = sliderWrapRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return rect.left + 14 + (pctRef.current / 100) * (rect.width - 28);
  }

  function handleTouchStart(e) {
    const touch   = e.touches[0];
    const thumbX  = getThumbClientX();
    const onThumb = Math.abs(touch.clientX - thumbX) < 24;
    const touchId = touch.identifier;

    touchMetaRef.current = { touchId, startX: touch.clientX, onThumb, moved: false };

    const rect = sliderWrapRef.current?.getBoundingClientRect();
    setPopupAnchorX(touch.clientX);
    setPopupAnchorY(rect ? rect.top : touch.clientY - 60);
    setShowPopup(true);

    function onMove(ev) {
      // Prevent pull-to-refresh and page scroll while the slider is active
      ev.preventDefault();
      const t = Array.from(ev.changedTouches).find(ct => ct.identifier === touchId);
      if (!t || !touchMetaRef.current) return;
      if (Math.abs(t.clientX - touchMetaRef.current.startX) > 5) {
        touchMetaRef.current.moved = true;
      }
      setPopupAnchorX(t.clientX);

      // Calculate new score from touch position and propagate it
      const el = sliderWrapRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const trackLeft = rect.left + 14;
        const trackWidth = rect.width - 28;
        const raw = (t.clientX - trackLeft) / trackWidth;
        const clamped = Math.max(0, Math.min(1, raw));
        const newVal = Math.round(clamped * 100) / 10; // 0.0–10.0, one decimal
        onChange(String(newVal));
      }
    }

    function onEnd(ev) {
      const t = Array.from(ev.changedTouches).find(ct => ct.identifier === touchId);
      if (!t) return;
      document.removeEventListener('touchmove',   onMove);
      document.removeEventListener('touchend',    onEnd);
      document.removeEventListener('touchcancel', onEnd);
      const meta = touchMetaRef.current;
      touchMetaRef.current = null;
      setShowPopup(false);
      if (meta?.onThumb && !meta.moved) setShowNumpad(true);
    }

    document.addEventListener('touchmove',   onMove, { passive: false });
    document.addEventListener('touchend',    onEnd);
    document.addEventListener('touchcancel', onEnd);
  }

  const trackGradient = `linear-gradient(to right, var(--c-action-bg) ${pct}%, rgba(255,255,255,0.2) ${pct}%)`;

  return (
    <>
      <div className="flex items-center gap-1.5 w-full">
        <span className="text-[10px] font-bold shrink-0 tabular-nums" style={{ color: 'var(--c-text-accent)' }}>0</span>
        <div
          ref={sliderWrapRef}
          className="flex-1 relative min-w-0 py-1"
          onTouchStart={isMobile ? handleTouchStart : undefined}
        >
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            value={numVal}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            className="score-slider w-full cursor-pointer relative z-10"
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              height: '4px',
              borderRadius: '2px',
              background: trackGradient,
              outline: 'none',
            }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none z-20 flex items-center justify-center rounded-full font-bold text-[10px] tabular-nums shadow-md"
            style={{
              width: 28,
              height: 28,
              left: `calc(${pct}% + ${14 - pct * 0.28}px)`,
              marginLeft: -14,
              background: 'var(--c-action-bg)',
              color: 'var(--c-action-fg)',
            }}
          >
            {displayVal}
          </div>
          <div className="absolute bottom-0 pointer-events-none flex justify-between" style={{ left: 14, right: 14 }}>
            {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
              <div key={n} className="flex flex-col items-center">
                <div style={{ width: 1, height: n % 5 === 0 ? 5 : 3, background: 'rgba(255,255,255,0.35)' }} />
              </div>
            ))}
          </div>
          <style>{`
            input[type=range].score-slider::-webkit-slider-thumb { opacity: 0; width: 28px; height: 28px; }
            input[type=range].score-slider::-moz-range-thumb { opacity: 0; width: 28px; height: 28px; border: none; }
          `}</style>
        </div>
        <span className="text-[10px] font-bold shrink-0 tabular-nums" style={{ color: 'var(--c-text-accent)' }}>10</span>
      </div>

      {isMobile && showPopup && createPortal(
        <SliderMagnifyPopup displayVal={displayVal} pct={pct} anchorX={popupAnchorX} anchorY={popupAnchorY} />,
        document.body
      )}

      {isMobile && showNumpad && createPortal(
        <NumpadModal
          value={value}
          onConfirm={(v) => { onChange(v); setShowNumpad(false); }}
          onClose={() => setShowNumpad(false)}
          t={t}
        />,
        document.body
      )}
    </>
  );
}

// Magnified popup shown above the finger while dragging on mobile
function SliderMagnifyPopup({ displayVal, pct, anchorX, anchorY }) {
  const POPUP_W   = 240;
  const POPUP_H   = 80;
  const TRACK_PAD = 14;
  const THUMB_W   = 16;
  const GAP       = 14;

  const left = Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, anchorX - POPUP_W / 2));
  const top  = anchorY - POPUP_H - GAP < 8
    ? anchorY + 40 + GAP
    : anchorY - POPUP_H - GAP;

  const trackInnerW = POPUP_W - TRACK_PAD * 2;
  const thumbLeft   = TRACK_PAD + (pct / 100) * trackInnerW - THUMB_W / 2;
  const trackGrad   = `linear-gradient(to right, var(--c-action-bg) ${pct}%, rgba(255,255,255,0.2) ${pct}%)`;

  return (
    <div
      aria-hidden="true"
      style={{ position: 'fixed', left, top, width: POPUP_W, zIndex: 9999, pointerEvents: 'none' }}
      className="t-bg-surface t-border border rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="flex items-baseline justify-center pt-2.5 pb-1 gap-0.5">
        <span className="font-black text-3xl tabular-nums leading-none" style={{ color: 'var(--c-action-bg)' }}>
          {displayVal}
        </span>
        <span className="text-sm t-text-muted">/10</span>
      </div>
      <div style={{ position: 'relative', height: 32, marginBottom: 6 }}>
        <div style={{
          position: 'absolute', left: TRACK_PAD, right: TRACK_PAD,
          top: '42%', transform: 'translateY(-50%)',
          height: 4, borderRadius: 2, background: trackGrad,
        }} />
        <div style={{
          position: 'absolute', top: '42%', transform: 'translateY(-50%)',
          width: THUMB_W, height: THUMB_W, borderRadius: '50%',
          left: thumbLeft, background: 'var(--c-action-bg)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }} />
        <div style={{
          position: 'absolute', left: TRACK_PAD, right: TRACK_PAD,
          bottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        }}>
          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
            <div key={n} style={{ width: 1, height: n % 5 === 0 ? 5 : 3, background: 'rgba(255,255,255,0.4)' }} />
          ))}
        </div>
        <div style={{
          position: 'absolute', left: TRACK_PAD - 2, right: TRACK_PAD - 2,
          bottom: -10, display: 'flex', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 9, color: 'var(--c-text-accent)', fontWeight: 700 }}>0</span>
          <span style={{ fontSize: 9, color: 'var(--c-text-accent)', fontWeight: 700 }}>10</span>
        </div>
      </div>
    </div>
  );
}

// Numpad modal for tapping the thumb on mobile
function NumpadModal({ value, onConfirm, onClose, t }) {
  const [input, setInput] = useState(() => {
    const n = parseFloat(value);
    return isNaN(n) ? '' : String(n);
  });

  function handleKey(key) {
    if (key === '⌫') { setInput(p => p.slice(0, -1)); return; }
    setInput(prev => {
      if (key === '.') {
        // No decimal after empty, already-decimal, or max value "10"
        if (prev === '' || prev.includes('.') || prev === '10') return prev;
        return prev + '.';
      }
      if (prev.includes('.')) {
        // After decimal: append one digit only
        if (prev.split('.')[1].length >= 1) return prev;
        return prev + key;
      }
      // Whole-number entry: each digit replaces, except "1"+"0" → "10"
      if (prev === '1' && key === '0') return '10';
      if (prev === '10') return prev; // already at max integer; ignore
      return key; // replace current integer with the new digit
    });
  }

  function handleConfirm() {
    let n = parseFloat(input);
    if (isNaN(n)) return;
    n = Math.max(0, Math.min(10, Math.round(n * 10) / 10));
    onConfirm(n.toFixed(1));
  }

  const numVal  = parseFloat(input);
  const isValid = !isNaN(numVal) && numVal >= 0 && numVal <= 10;
  const KEYS    = ['7','8','9','4','5','6','1','2','3','.','0','⌫'];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)' }}
      className="flex items-end sm:items-center justify-center p-4"
      onPointerDown={onClose}
    >
      <div
        className="t-bg-surface t-border border rounded-2xl p-4 shadow-2xl w-full max-w-xs"
        onPointerDown={e => e.stopPropagation()}
      >
        <p className="text-center text-xs t-text-muted mb-2 font-medium uppercase tracking-wider">
          {t('scoring.numpadTitle')}
        </p>
        <div className="flex items-baseline justify-center gap-1 mb-4 min-h-14">
          <span className="font-black text-5xl tabular-nums leading-none" style={{ color: 'var(--c-action-bg)' }}>
            {input || '–'}
          </span>
          <span className="text-xl t-text-muted">/10</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {KEYS.map(key => (
            <button
              key={key}
              type="button"
              className="t-bg-input t-border border rounded-xl py-3.5 text-xl font-semibold t-text active:opacity-60 t-focus-ring select-none"
              onPointerDown={(e) => { e.preventDefault(); handleKey(key); }}
            >
              {key}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 t-bg-input t-border border rounded-xl py-3 t-text-muted font-medium t-focus-ring active:opacity-60"
            onClick={onClose}
          >
            {t('scoring.numpadCancel')}
          </button>
          <button
            type="button"
            className="flex-2 t-btn-primary rounded-xl py-3 font-semibold t-focus-ring disabled:opacity-40"
            onClick={handleConfirm}
            disabled={!isValid}
          >
            {t('scoring.numpadConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

